"use client";

import React, { useMemo } from "react";
import { useFieldArray, UseFormReturn, useWatch } from "react-hook-form";
import { z as zod } from "zod";

import {
	Alert,
	Box,
	CardContent,
	Radio,
	Stack,
	Typography,
} from "@mui/material";

import { useResponsive } from "@onair/minimal-ui/hooks/use-responsive";
import { Iconify } from "@onair/minimal-ui/iconify";
import {
	AddressScope,
	LocationType,
	SessionPriceType,
	TicketScope,
} from "@onair/repositories";

import CardWizard from "./card-wizard";
import EventSessionPricingAll from "./event-sessions/event-session-pricing-all";
import EventSessionPricingEach from "./event-sessions/event-session-pricing-each";
import EventSessionRoomItem from "./event-sessions/event-session-room-item";
import { WizardSchemaType } from "./index";
import PreviewInfo from "./preview/info";
import SocialCommunitySection from "./social-community-section";
import { EventSessionPricingSchema } from "./step-timeline";
import {
	TicketTierOfflineSchema,
	TicketTierOnlineSchema,
} from "./validations/ticket-tier-schema.ts";
import { SocialCommunitiesSchema } from "./validations/social-communities-schema.ts";
import TicketScopeBox from "./ticket-tiers/ticket-scope-box.tsx";
import InputLabelWizard from "./input-label-wizard.tsx";
import AddressScopeBox from "./address/address-scope-box.tsx";
import isUndefined from "lodash/isUndefined";

interface PropTypes {
	methods: UseFormReturn<WizardSchemaType>;
	navigator?: React.ReactNode;
}

const StepPricingOnEachSchema = zod.object({
	priceType: zod.literal(SessionPriceType.EACH),
	locationType: zod.nativeEnum(LocationType, {
		required_error: "Vui lòng chọn loại địa điểm",
		invalid_type_error: "Vui lòng chọn loại địa điểm",
	}),
	addressScope: zod.nativeEnum(AddressScope),
	ticketScope: zod.nativeEnum(TicketScope, {
		required_error: "Vui lòng chọn phạm vi vé",
		invalid_type_error: "Vui lòng chọn phạm vi vé",
	}),
	sessions: zod.array(EventSessionPricingSchema),
	socialCommunities: zod.array(SocialCommunitiesSchema),
	ticketTiers: zod.array(zod.any()).optional(),
	address: zod.string().optional(),
});

const StepPricingOnAllSchema = zod.object({
	priceType: zod.literal(SessionPriceType.ALL),
	locationType: zod.nativeEnum(LocationType, {
		required_error: "Vui lòng chọn loại địa điểm",
		invalid_type_error: "Vui lòng chọn loại địa điểm",
	}),
	addressScope: zod.nativeEnum(AddressScope),
	ticketScope: zod.nativeEnum(TicketScope, {
		required_error: "Vui lòng chọn phạm vi vé",
		invalid_type_error: "Vui lòng chọn phạm vi vé",
	}),
	sessions: zod.array(
		zod.object({
			roomProvider: zod.any(),
			roomUrl: zod.string().url().nullable().optional(),
			roomInfo: zod
				.object({
					id: zod.string().optional(),
					password: zod.string().optional(),
				})
				.optional(),
			ticketTiers: zod.array(zod.any()).optional(),
			address: zod.string().optional(),
		}),
	),
	socialCommunities: zod.array(SocialCommunitiesSchema),
	ticketTiers: zod.array(zod.any()).optional(),
	address: zod.string().optional(),
});

const CondSchema = zod.discriminatedUnion("priceType", [
	StepPricingOnEachSchema,
	StepPricingOnAllSchema,
]);

export const StepPricingSchema = CondSchema.refine(
	(data) => {
		return (
			!data.socialCommunities.some((item) => item.socialCommunityLink) ||
			!!data.socialCommunities.some((item) => item.socialCommunityName)
		);
	},
	{
		message: "Vui lòng nhập tên nhóm cộng đồng",
		path: ["socialCommunityName"],
	},
).superRefine((data, ctx) => {
	if (data.locationType === LocationType.ONLINE) {
		data.sessions.forEach((session, index) => {
			if (!session.roomProvider) {
				ctx.addIssue({
					code: zod.ZodIssueCode.custom,
					message: "Vui lòng chọn nền tảng tổ chức sự kiện",
					path: ["sessions", index, "roomProvider"],
				});
			}

			if (!session.roomUrl) {
				ctx.addIssue({
					code: zod.ZodIssueCode.custom,
					message: "Vui lòng nhập link tham gia sự kiện",
					path: ["sessions", index, "roomUrl"],
				});
			}
		});

		if (data.priceType === SessionPriceType.ALL) {
			if (isUndefined(data.ticketTiers) || !data.ticketTiers.length) {
				ctx.addIssue({
					code: zod.ZodIssueCode.custom,
					message: "Vui lòng thêm ít nhất một loại vé",
					path: ["ticketTiers"],
				});
			}

			if (data?.ticketTiers?.length) {
				try {
					TicketTierOnlineSchema.parse(data?.ticketTiers?.[0]);
				} catch (e) {
					if (e instanceof zod.ZodError) {
						const issues = e.issues;

						issues.forEach((issue) => {
							ctx.addIssue({
								code: zod.ZodIssueCode.custom,
								message: issue.message,
								path: ["ticketTiers", 0, ...issue.path],
							});
						});
					}
				}
			}
		}

		if (data.priceType === SessionPriceType.EACH) {
			data.sessions.forEach((session, index) => {
				if (isUndefined(session.ticketTiers) || !session.ticketTiers.length) {
					ctx.addIssue({
						code: zod.ZodIssueCode.custom,
						message: "Vui lòng thêm ít nhất một loại vé",
						path: ["sessions", 0, "ticketTiers"],
					});
				}

				if (session?.ticketTiers?.length) {
					try {
						TicketTierOnlineSchema.parse(session?.ticketTiers[0]);
					} catch (e) {
						if (e instanceof zod.ZodError) {
							const issues = e.issues;

							issues.forEach((issue) => {
								ctx.addIssue({
									code: zod.ZodIssueCode.custom,
									message: issue.message,
									path: ["sessions", index, "ticketTiers", 0, ...issue.path],
								});
							});
						}
					}
				}
			});
		}
	}

	if (
		data.locationType === LocationType.OFFLINE &&
		data.ticketScope === TicketScope.EACH
	) {
		data.sessions.forEach((session, index) => {
			if (isUndefined(session.ticketTiers) || !session.ticketTiers.length) {
				ctx.addIssue({
					code: zod.ZodIssueCode.custom,
					message: `Vui lòng thêm ít nhất một loại vé cho sự kiện ${index + 1}`,
					path: ["sessions", index, "ticketTiers"],
				});
			}

			if (session?.ticketTiers?.length) {
				session.ticketTiers.forEach((ticketTier, ticketTierIndex) => {
					try {
						TicketTierOfflineSchema.parse(ticketTier);
					} catch (e) {
						if (e instanceof zod.ZodError) {
							const issues = e.issues;

							issues.forEach((issue) => {
								ctx.addIssue({
									code: zod.ZodIssueCode.custom,
									message: issue.message,
									path: [
										"sessions",
										index,
										"ticketTiers",
										ticketTierIndex,
										...issue.path,
									],
								});
							});
						}
					}
				});
			}
		});
	}

	if (
		data.locationType === LocationType.OFFLINE &&
		data.ticketScope === TicketScope.ALL
	) {
		if (isUndefined(data.ticketTiers) || !data.ticketTiers.length) {
			ctx.addIssue({
				code: zod.ZodIssueCode.custom,
				message: "Vui lòng thêm ít nhất một loại vé",
				path: ["ticketTiers"],
			});
		}

		if (data?.ticketTiers?.length) {
			try {
				TicketTierOfflineSchema.parse(data?.ticketTiers[0]);
			} catch (e) {
				if (e instanceof zod.ZodError) {
					const issues = e.issues;

					issues.forEach((issue) => {
						ctx.addIssue({
							code: zod.ZodIssueCode.custom,
							message: issue.message,
							path: ["ticketTiers", 0, ...issue.path],
						});
					});
				}
			}
		}
	}

	if (
		data.locationType === LocationType.OFFLINE &&
		data.addressScope === AddressScope.ALL &&
		!data.address
	) {
		ctx.addIssue({
			code: zod.ZodIssueCode.custom,
			message: "Vui lòng nhập địa chỉ tổ chức sự kiện",
			path: ["address"],
		});
	}

	if (
		data.locationType === LocationType.OFFLINE &&
		data.addressScope === AddressScope.EACH
	) {
		data.sessions.forEach((session, index) => {
			if (!session.address) {
				ctx.addIssue({
					code: zod.ZodIssueCode.custom,
					message: `Vui lòng nhập địa chỉ tổ chức sự kiện cho sự kiện ${index + 1}`,
					path: ["sessions", index, "address"],
				});
			}
		});
	}
});

interface PriceTypeSelectionProps {
	priceType: SessionPriceType;
	value: SessionPriceType;
	onSelect: (value: SessionPriceType) => void;
	disabled?: boolean;
}

const PriceTypeSelection = ({
	priceType,
	value,
	onSelect,
	disabled = false,
}: PriceTypeSelectionProps) => {
	const isSelected = priceType === value;

	let label = "Theo toàn chuỗi";
	let description =
		"User bắt buộc phải mua nguyên chuỗi sự kiện, không thể mua theo sự kiện lẻ";
	let icon = "majesticons:creditcard-line";

	if (priceType === SessionPriceType.EACH) {
		label = "Theo từng sự kiện";
		description =
			"User có thể mua từng sự kiện lẻ, không bắt buộc mua toàn chuỗi sự kiện";
		icon = "ic:outline-diamond";
	}

	const handleSelect = () => {
		if (!disabled) {
			onSelect(priceType);
		}
	};

	return (
		<Box
			sx={{
				border: "1px solid",
				borderColor: isSelected ? "primary.main" : "divider",
				borderRadius: 2,
				flexGrow: 1,
				cursor: disabled ? "not-allowed" : "pointer",
				opacity: disabled ? 0.65 : 1,
			}}
			onClick={handleSelect}
			px={{
				xs: 2,
				md: 3,
			}}
			py={2}
		>
			<Stack direction="row" justifyContent="space-between" alignItems="center">
				<Stack direction="row" spacing={1} alignItems="center">
					<Iconify
						color={isSelected ? "primary.main" : "text.primary"}
						icon={icon}
						width={24}
						height={24}
					/>
					<Typography
						display={{ xs: "none", md: "block" }}
						color={isSelected ? "primary.main" : "text.primary"}
						variant="body2"
					>
						{label}
					</Typography>
				</Stack>
				<Radio checked={isSelected} size="medium" />
			</Stack>
			<Typography
				display={{ xs: "block", md: "none" }}
				color={isSelected ? "primary.main" : "text.primary"}
				variant="body2"
			>
				{label}
			</Typography>

			<Typography color="text.secondary" sx={{ fontSize: "12px" }}>
				{description}
			</Typography>
		</Box>
	);
};

const StepPricing: React.FC<PropTypes> = ({ methods, navigator }) => {
	const { setValue, getValues, control } = methods;

	const mdDown = useResponsive("down", "md");
	const { fields } = useFieldArray({
		control,
		name: "stepTimeline.sessions",
	});

	const priceType = useWatch({
		name: "stepPricing.priceType",
		control,
	});

	const locationType = useWatch({
		name: "locationType",
		control,
	});

	const ticketScope = useWatch({
		name: "stepPricing.ticketScope",
		control,
	});

	const eventSessions = getValues("stepPricing.sessions");

	const hasBeenPaid = useMemo(() => {
		if (
			priceType === SessionPriceType.ALL &&
			locationType === LocationType.ONLINE
		) {
			return getValues("stepPricing.ticketTiers.0.hasBeenPaid");
		}

		if (
			ticketScope === TicketScope.ALL &&
			locationType === LocationType.OFFLINE
		) {
			return getValues("stepPricing.ticketTiers")?.some(
				(ticketTier) => ticketTier.hasBeenPaid,
			);
		}

		return eventSessions.some((session) => {
			return session.ticketTiers?.some((ticketTier) => ticketTier.hasBeenPaid);
		});
	}, [priceType, locationType, ticketScope]);

	const isOffline = locationType === LocationType.OFFLINE;

	const data = useWatch({
		control,
	});

	return (
		<Stack direction="row" spacing={3}>
			<Stack flex={1}>
				<Stack spacing={3}>
					<CardWizard>
						<CardContent>
							<Stack spacing={3}>
								<Stack
									spacing={{
										xs: 2,
										md: 3,
									}}
								>
									<Typography fontWeight={600} variant="body1">
										Phương thức tính giá vé
									</Typography>

									{!isOffline && (
										<Alert variant="outlined" severity="info">
											Bạn có thể cấu hình Mã giảm giá ở phần cài đặt Voucher.
										</Alert>
									)}

									<Stack direction="row" spacing={2}>
										<PriceTypeSelection
											priceType={SessionPriceType.ALL}
											value={priceType}
											onSelect={(value) =>
												setValue("stepPricing.priceType", value)
											}
											disabled={hasBeenPaid}
										/>
										<PriceTypeSelection
											priceType={SessionPriceType.EACH}
											value={priceType}
											onSelect={(value) =>
												setValue("stepPricing.priceType", value)
											}
											disabled={hasBeenPaid}
										/>
									</Stack>

									{isOffline ? (
										<TicketScopeBox disabled={hasBeenPaid} />
									) : (
										<>
											{priceType === SessionPriceType.ALL && (
												<EventSessionPricingAll methods={methods} />
											)}
											{priceType === SessionPriceType.EACH && (
												<EventSessionPricingEach methods={methods} />
											)}
										</>
									)}
								</Stack>
							</Stack>
						</CardContent>
					</CardWizard>
					{!isOffline && (
						<CardWizard>
							<CardContent>
								<Stack spacing={3}>
									<Stack
										spacing={{
											xs: 2,
											md: 3,
										}}
									>
										<Typography fontWeight={600} variant="body1">
											Link tham gia
										</Typography>

										<Stack spacing={2}>
											{fields.map((field, index) => {
												return (
													<EventSessionRoomItem
														key={field.id}
														index={index}
														methods={methods}
													/>
												);
											})}
										</Stack>
									</Stack>
								</Stack>
							</CardContent>
						</CardWizard>
					)}
					{isOffline && (
						<CardWizard>
							<CardContent>
								<Stack spacing={3}>
									<Stack
										spacing={{
											xs: 2,
											md: 3,
										}}
									>
										<InputLabelWizard required>
											Địa điểm tổ chức
										</InputLabelWizard>
										<AddressScopeBox />
									</Stack>
								</Stack>
							</CardContent>
						</CardWizard>
					)}

					<CardWizard>
						<CardContent>
							<Stack spacing={3}>
								<Stack
									spacing={{
										xs: 2,
										md: 3,
									}}
								>
									<SocialCommunitySection prefixName="stepPricing" />
								</Stack>
							</Stack>
						</CardContent>
					</CardWizard>
				</Stack>
				{navigator}
			</Stack>
			{!mdDown && <PreviewInfo data={data as WizardSchemaType} />}
		</Stack>
	);
};

export default StepPricing;
